import { jwtDecode } from 'jwt-decode';
import { PLATFORM_ROLE_TYPE } from '@/consts/common';
import { CustomJwtPayload } from '@/utils/helpers/authHelpers';
import { safeJsonParse } from '@/utils/helpers/commonHelpers';
import { withSafeResultSync } from '@/utils/helpers/safeResult';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import { IPlatformRole, PlatformRoleType } from '@/services/users/types';
import { IAuthRole } from './types';
import { LICENSE_PRODUCT } from '@/consts/licenseManagement';

type JwtPlatformRole = IPlatformRole & { role_name?: PlatformRoleType };

/** Normalizes JWT platform-role entries that may use snake_case or camelCase keys. */
function getRoleNameFromJwtEntry(platform: JwtPlatformRole): PlatformRoleType | undefined {
  return platform.roleName ?? platform.role_name;
}

/** Reads HoloXR role names from the JWT without applying the base `user` role. */
export function getJwtPlatformRoles(): PlatformRoleType[] {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return [];

  const platformRolesResult = withSafeResultSync(() => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:platform_roles'];
  });
  if (!platformRolesResult.ok) return [];

  const platformRoles = safeJsonParse<JwtPlatformRole[]>(platformRolesResult.data);
  return platformRoles
    ?.filter((platform) => platform.product === LICENSE_PRODUCT.HOLOXR)
    .map(getRoleNameFromJwtEntry)
    .filter((roleName): roleName is PlatformRoleType => !!roleName) ?? [];
}

/**
 * Returns effective viewer roles for permission checks.
 * Every account implicitly has `user`; elevated JWT roles are additive.
 */
export function getViewerRoles(): IAuthRole[] {
  const jwtRoles = getJwtPlatformRoles();
  const elevatedRoles = jwtRoles.filter((role) => role !== PLATFORM_ROLE_TYPE.USER);

  if (elevatedRoles.length === 0) {
    return [PLATFORM_ROLE_TYPE.USER];
  }

  return [PLATFORM_ROLE_TYPE.USER, ...elevatedRoles];
}
